import Autocomplete from '@trevoreyre/autocomplete-js';
import {Country} from '../interfaces/country.interface';

export class CountriesSearchAutocomplete {
    readonly autocompleteComponentId = 'countries-typeahead';
    readonly autocompleteInputId = 'countries-hero-input';

    private autocompleteComponent: HTMLDivElement;
    private autocompleteInput: HTMLInputElement;
    private autocompleteResults: HTMLUListElement;

    private countriesList: Country[] = [];

    constructor() {
        this.countriesList = window.skyConstants.countries;
    }

    public init(): void {
        this.getElementsReferences();
        this.initAutocomplete(this.autocompleteComponentId);
    }

    private searchCountries(searchTerm: string): Country[] {
        if (searchTerm.length < 1) {
            return []
        }
        return this.countriesList.filter(country => {
            return country.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
        })
    }

    private initAutocomplete(id: string): void {
        new Autocomplete(`#${id}`, {
            search: this.searchCountries.bind(this),
            renderResult: this.getRenderResult.bind(this),
            getResultValue: this.getResultValue.bind(this),
            onSubmit: this.onSubmit.bind(this),
            submitOnEnter: true
        });
        // The library only sets aria-activedescendant once the result list is first hidden,
        // but its Enter keydown handler reads getAttribute('aria-activedescendant').length.
        // Pressing Enter before the first search cycle would read length on null.
        this.autocompleteInput.setAttribute('aria-activedescendant', '');
        this.autocompleteResults.classList.add('d-block');
        this.setArrowKeyNavigation();
    }

    private getResultValue(selectedCountry: Country): string {
        return selectedCountry.name;
    }

    private onSubmit(selectedCountry: Country): void {
        const hiddenInput: HTMLInputElement = this.autocompleteComponent.querySelector('input[type="hidden"]');
        hiddenInput.value = selectedCountry.iso;
    }

    private getRenderResult(selectedCountry: Country, props: string): string {
        return `<li class="dropdown-item" title="${selectedCountry.name}" ${props}>
            <span class="sd-mr-0_5 font-size-md">${this.getFlagEmoji(selectedCountry.iso)}</span>${selectedCountry.name}
        </li>`;
    }

    private getElementsReferences(): void {
        if (!this.autocompleteComponent) {
            this.autocompleteComponent = document.querySelector(`#${this.autocompleteComponentId}`);
        }
        if (!this.autocompleteInput) {
            this.autocompleteInput = this.autocompleteComponent.querySelector(`#${this.autocompleteInputId}`);
        }
        if (!this.autocompleteResults) {
            this.autocompleteResults = this.autocompleteComponent.querySelector('ul');
        }
    }

    private getFlagEmoji(countryCode): string {
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char =>  127397 + Number(char.charCodeAt()), 10);
        return String.fromCodePoint(...codePoints);
    }

    private setArrowKeyNavigation(): void {
        this.autocompleteInput.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                this.autocompleteResults.querySelectorAll('li').forEach((li) => {
                    if (li.hasAttribute('aria-selected')) {
                        li.classList.add('active');
                    } else {
                        li.classList.remove('active');
                    }
                });
            }
        });
    }
}

const countriesSearchAutocomplete = new CountriesSearchAutocomplete();
window.addEventListener('load', () => {
    countriesSearchAutocomplete.init();
});
